/* ===========================================================
   ui/context.jsx — Docs Protocol's own right-click menu (his ask,
   2026-10-28; Notes and Drive get the same one)

   In your writing: cut / copy / paste, formatting, headings, lists,
   plus link and table options when you're on one. On a doc in the
   list: that doc's options. Anywhere else: new doc, your docs,
   settings. Text boxes keep the browser's menu, Shift + right-click
   always gives the browser's menu, and on phones a long-press in the
   writing keeps the phone's own copy/paste bar.
   =========================================================== */

import { signal } from '@preact/signals';
import { FloatingMenu, toast } from './ui.jsx';
import { go } from '../lib/router.js';
import { docs } from '../state.js';
import { addWord, report, isMine } from '../sync/words.js';

let editorRef = null;
/** EditorView calls this so the menu knows which editor you're in. */
export const setActiveEditor = ed => { editorRef = ed; };

/** Pages add their own items for an element: register(selector, el => items). */
const providers = [];
export function registerContext(selector, build) { providers.push({ selector, build }); }

const open = signal(null);   // { x, y, items, label }

const coarse = () => typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

function clip(cmd, ed) {
  ed.view.focus();
  // a menu click counts as you pressing the keys, so the browser allows it
  if (!document.execCommand(cmd)) toast(`Press Ctrl+${cmd === 'cut' ? 'X' : 'C'} to ${cmd}.`);
}

async function paste(ed, plain) {
  ed.view.focus();
  try {
    if (!plain && navigator.clipboard?.read) {
      for (const item of await navigator.clipboard.read()) {
        if (item.types.includes('text/html')) { ed.view.pasteHTML(await (await item.getType('text/html')).text()); return; }
      }
    }
    ed.view.pasteText(await navigator.clipboard.readText());
  } catch {
    toast('Your browser keeps the clipboard private here. Press Ctrl+V to paste (Ctrl+Shift+V without formatting).');
  }
}

function editorItems(ed) {
  const has = !ed.state.selection.empty;
  const can = ed.isEditable;
  const link = ed.isActive('link') ? ed.getAttributes('link').href : null;
  const table = can && ed.isActive('table');
  const c = () => ed.chain().focus();
  return [
    link && { label: 'Open link', icon: 'open', onSelect: () => window.open(link, '_blank', 'noopener') },
    link && { label: 'Copy link', icon: 'link', onSelect: () => navigator.clipboard.writeText(link).then(() => toast('Link copied.'), () => toast(link)) },
    link && can && { label: 'Remove link', icon: 'unlink', onSelect: () => c().extendMarkRange('link').unsetLink().run() },
    link && 'sep',
    can && { label: 'Cut', icon: 'eraser', hint: 'Ctrl+X', disabled: !has, onSelect: () => clip('cut', ed) },
    { label: 'Copy', icon: 'copy', hint: 'Ctrl+C', disabled: !has, onSelect: () => clip('copy', ed) },
    can && { label: 'Paste', icon: 'download', hint: 'Ctrl+V', onSelect: () => paste(ed, false) },
    can && { label: 'Paste without formatting', hint: 'Ctrl+Shift+V', onSelect: () => paste(ed, true) },
    can && 'sep',
    can && has && { label: 'Bold', icon: 'bold', hint: 'Ctrl+B', active: ed.isActive('bold'), onSelect: () => c().toggleBold().run() },
    can && has && { label: 'Italic', icon: 'italic', hint: 'Ctrl+I', active: ed.isActive('italic'), onSelect: () => c().toggleItalic().run() },
    can && has && !link && { label: 'Add a link…', icon: 'link', onSelect: () => import('../editor/Toolbar.jsx').then(m => m.askLink(ed)) },
    can && { label: 'Clear formatting', icon: 'clearFormat', onSelect: () => c().unsetAllMarks().clearNodes().run() },
    can && 'sep',
    can && { label: 'Normal text', icon: 'text', active: ed.isActive('paragraph'), onSelect: () => c().setParagraph().run() },
    can && { label: 'Heading 1', icon: 'heading', active: ed.isActive('heading', { level: 1 }), onSelect: () => c().toggleHeading({ level: 1 }).run() },
    can && { label: 'Heading 2', icon: 'heading', active: ed.isActive('heading', { level: 2 }), onSelect: () => c().toggleHeading({ level: 2 }).run() },
    can && { label: 'Bulleted list', icon: 'bullets', active: ed.isActive('bulletList'), onSelect: () => c().toggleBulletList().run() },
    can && { label: 'Numbered list', icon: 'numbers', active: ed.isActive('orderedList'), onSelect: () => c().toggleOrderedList().run() },
    table && 'sep',
    table && { heading: 'Table' },
    table && { label: 'Row above', icon: 'plus', onSelect: () => c().addRowBefore().run() },
    table && { label: 'Row below', icon: 'plus', onSelect: () => c().addRowAfter().run() },
    table && { label: 'Column left', icon: 'plus', onSelect: () => c().addColumnBefore().run() },
    table && { label: 'Column right', icon: 'plus', onSelect: () => c().addColumnAfter().run() },
    table && { label: 'Delete row', icon: 'trash', onSelect: () => c().deleteRow().run() },
    table && { label: 'Delete column', icon: 'trash', onSelect: () => c().deleteColumn().run() },
    table && { label: 'Delete table', icon: 'trash', danger: true, onSelect: () => c().deleteTable().run() },
    'sep',
    { label: 'Select all', hint: 'Ctrl+A', onSelect: () => c().selectAll().run() },
  ];
}

function appItems() {
  const inDoc = location.pathname.startsWith('/d/');
  return [
    { label: 'New blank document', icon: 'plus', onSelect: () => import('../sync/docs.js').then(async m => { const d = await m.createDoc(); go(`/d/${d.id}`); }) },
    inDoc && { label: 'Back to your docs', icon: 'back', onSelect: () => go('/') },
    !inDoc && { label: 'Your docs', icon: 'doc', onSelect: () => go('/') },
    { label: 'Open from computer', icon: 'computer', onSelect: () => go('/files') },
    'sep',
    { label: 'Settings', icon: 'settings', onSelect: () => go('/settings') },
    { label: 'Reload', icon: 'refresh', onSelect: () => location.reload() },
  ];
}

/** The word under the mouse, if it's a plain word. */
function wordAt(e) {
  const r = document.caretRangeFromPoint?.(e.clientX, e.clientY);
  const node = r?.startContainer;
  if (!node || node.nodeType !== 3) return null;
  const text = node.textContent, i = r.startOffset;
  let a = i, b = i;
  while (a > 0 && /[A-Za-z'’-]/.test(text[a - 1])) a--;
  while (b < text.length && /[A-Za-z'’-]/.test(text[b])) b++;
  const w = text.slice(a, b).replace(/^['’-]+|['’-]+$/g, '');
  return w.length >= 2 && w.length <= 40 ? w : null;
}

function onContext(e) {
  if (e.shiftKey || e.defaultPrevented) return;                     // Shift: the browser's own menu
  const t = e.target;
  if (t.closest?.('input, textarea, select, [data-native-menu], .cm-native')) return;
  let items = null, label = 'Menu';
  for (const p of providers) {
    const el = t.closest?.(p.selector);
    if (el) { items = p.build(el, e); label = 'Options'; if (items) break; }
  }
  const maybe = t.closest?.('.ac-maybe');
  if (!items && maybe && editorRef?.isEditable) {
    // a word autocorrect wasn't sure about: pick which one you meant
    const from = editorRef.view.posAtDOM(maybe, 0), to = from + maybe.textContent.length;
    const word = maybe.textContent;
    items = [{ heading: 'Did you mean' }, ...maybe.dataset.ac.split('|').map(w => ({ label: w, onSelect: () => { report(word, w, 'picked'); editorRef.chain().focus().insertContentAt({ from, to }, w).run(); } })),
      { label: `Add "${word}" to my dictionary`, icon: 'plus', onSelect: () => addWord(word, maybe.dataset.ac.split('|')[0]).then(() => toast(`"${word}" is in your dictionary. Autocorrect leaves it alone now.`), err => toast(err, { kind: 'error' })) }, 'sep'];
    items.push(...editorItems(editorRef));
  }
  if (!items && t.closest?.('.ProseMirror') && editorRef) {
    if (coarse()) return;                                             // phones: keep the native copy/paste bar
    items = editorItems(editorRef);
    label = 'Edit';
    const w = wordAt(e);
    if (w && editorRef.isEditable && !isMine(w)) items = [{ label: `Add "${w}" to my dictionary`, icon: 'plus', onSelect: () => addWord(w).then(() => toast(`"${w}" is in your dictionary.`), err => toast(err, { kind: 'error' })) }, 'sep', ...items];
  }
  if (!items) items = appItems();
  e.preventDefault();
  open.value = { x: e.clientX, y: e.clientY, items, label };
}

if (typeof document !== 'undefined') document.addEventListener('contextmenu', onContext);

/** Mounted once in app.jsx. */
export function ContextMenuHost() {
  const m = open.value;
  if (!m) return null;
  return <FloatingMenu items={m.items} label={m.label} at={{ x: m.x, y: m.y }} onClose={() => { open.value = null; }} key={`${m.x},${m.y}`} />;
}

export const findDocInList = id => docs.value.find(d => d.id === id);
